import { ID_OFFSET, READABLE_ID_MAX_LENGTH } from '@/libs/config/client'
import { cloneArray, min } from '@/utils/array'
import { isObjectEmpty } from '@/utils/object'
import React from 'react'
import { getTimestampOfMidnightForCountry } from '@/utils/date'

interface Sections {
  header?: {
    homepageUrl?: string
  }
}

export const productStatus = {
  AVAILABLE: 0,
  UNAVAILABLE: 1,
}

export const makeReadableId = (
  id: string,
  name: string,
  idOffset = ID_OFFSET,
  readableIdMaxLength = READABLE_ID_MAX_LENGTH
): string =>
  `${id.slice(-1 * idOffset)}-${name
    .toLowerCase()
    .replace(/[^a-zA-Z0-9-_ ]/g, '')
    .replace(/ /g, '-')}`.substring(0, readableIdMaxLength)

export const injectReadableIds = (
  groups: Oms.MenuCategory[]
): Oms.MenuCategory[] => {
  return (groups || []).map((group) => {
    group.items = (group.items || []).map((item) => {
      item.readableId = makeReadableId(item.id, item.name)
      return item
    })
    return group
  })
}

export const findItem = (
  groups: Oms.MenuCategory[],
  checker: (item: Oms.MenuItem) => boolean
): Oms.MenuItem | null => {
  let item = null
  groups?.some((g) => {
    const found = g.items.find(checker)
    if (found) {
      item = found
      return true
    }
    return false
  })
  return item
}

export const findVariantsItemFromGroups = (
  groups: Oms.MenuCategory[],
  value: string
) =>
  findItem(
    groups,
    (item) =>
      item.variants?.some((variant: { id: string }) => variant.id === value) ??
      false
  )

export const getFullProductDetails = (
  menu: Oms.MenuCategory[] | null,
  item: Oms.OrderItem
) => {
  if (!menu) return item
  const fullItem = findVariantsItemFromGroups(menu, item.itemReference)
  return fullItem ? { ...item, details: fullItem } : item
}

export const getOrderItemQuantity = (orderItems: Oms.OrderItem[]): number =>
  orderItems.reduce((sum, order) => +order.quantity + sum, 0)

export const getDisplayPriceFromItem = (
  t: (t: string) => string,
  product: Oms.MenuItem,
  formatMoney = (x: number) => x.toString()
): {
  preposition: string
  displayPrice: string
} => {
  const { variants = [], price } = product
  const displayPrice =
    typeof price === 'number'
      ? formatMoney(price)
      : formatMoney(price || getItemPrice({ variants }))
  return variants.length > 1
    ? { preposition: t('From'), displayPrice }
    : { preposition: '', displayPrice }
}

export const getItemPrice = ({
  variants = [],
}: {
  variants: Oms.Variant[]
}): number =>
  variants.length > 1
    ? min(
        variants
          .map((v) => v.price)
          .filter((price): price is number => price !== null)
      )
    : parseFloat(((variants[0] || {}).price || 0).toString())

export const getOrderItem = (
  product: Oms.MenuItem,
  totalQuantity: number,
  selectedVariant: string,
  subitemsMap: Record<string, Record<string, number>>
): {
  variantId: string
  quantity: number
  subitems: Array<{
    subitemId: string
    variants: Array<{
      variantId: string
      quantity: number
    }>
  }>
} => {
  const subitems = Object.keys(subitemsMap || {})
    .map((subitemId) => {
      const variants = Object.keys(subitemsMap[subitemId] || {})
        .map((variantId) => ({
          variantId,
          quantity: subitemsMap[subitemId]?.[variantId] || 0,
        }))
        .filter(({ quantity }) => quantity > 0)

      return { subitemId, variants }
    })
    .filter(({ variants }) => variants.length > 0)

  return {
    variantId: selectedVariant || product.variants?.[0]?.id || '',
    quantity: totalQuantity,
    subitems,
  }
}

export const getProductButtonProps = (
  t: (
    t: string,
    options?: Record<string, string>
  ) => string | React.ReactNodeArray,
  product: Oms.MenuItem,
  category: Oms.MenuCategory,
  storeItemAvailability: {
    selectedStore?: Oms.Store | null
    isUnavailableAtSelectedStore: boolean
    isUnavailableAtAllStores: boolean
    isUnavailableAtSomeStores: boolean
    availableStores: Oms.Store[]
    isPickup: boolean
    initialized: boolean
    isPermanentlyUnavailable: boolean
    isTemporarilyUnavailable: boolean
  },
  enableTimeLimitUnavailability: boolean,
  gmtOffset?: number
): { text: string; disabled: boolean; loading: boolean } => {
  const item = category.status === productStatus.AVAILABLE ? product : category
  const {
    selectedStore,
    isUnavailableAtSelectedStore,
    isUnavailableAtAllStores,
    isUnavailableAtSomeStores,
    availableStores,
    initialized,
    isPermanentlyUnavailable,
    isTemporarilyUnavailable,
  } = storeItemAvailability

  const isUnavailableAllDays = item.availableDays === ''
  const isUnavailableSomeDays =
    typeof item.availableDays === 'string' && item.availableDays.length > 0
  const isFutureItem = Boolean(
    item.collectionStartDate ||
      item.collectionEndDate ||
      item.collectionStartTime ||
      item.collectionEndTime
  )

  const getMuteUntilText = (): string => {
    if (item.muteUntil === null) return t('Out of stock') as string

    const muteUntilNumber = Number.parseInt(item.muteUntil || '0', 10)
    const muteUntil = new Date(muteUntilNumber)
    const currentDate = new Date()
    const midnightForCountry = getTimestampOfMidnightForCountry(gmtOffset)

    if (midnightForCountry === muteUntilNumber) {
      return t('Out of stock today') as string
    }
    if (currentDate.getDate() === muteUntil.getDate()) {
      return t('Out until {hour}', {
        hour: muteUntil.toLocaleTimeString(),
      }) as string
    }
    return t('Out of stock') as string
  }

  // Handle store availability cases
  if (isUnavailableAtAllStores) {
    return {
      text:
        isTemporarilyUnavailable && !isPermanentlyUnavailable
          ? (t('Out of stock today') as string)
          : (t('Out of stock') as string),
      disabled: true,
      loading: !initialized,
    }
  }

  if (isUnavailableAtSelectedStore) {
    if (enableTimeLimitUnavailability) {
      return { text: getMuteUntilText(), disabled: true, loading: !initialized }
    }
    return {
      text: t('Out of stock') as string,
      disabled: !isObjectEmpty(selectedStore),
      loading: !initialized,
    }
  }

  if (isUnavailableAtSomeStores && !selectedStore) {
    const openAvailableStores = availableStores.filter((store) => store.open)
    return {
      text:
        openAvailableStores.length > 0
          ? (t('Subject to store availability') as string)
          : (t('Out of stock') as string),
      disabled: openAvailableStores.length <= 0,
      loading: !initialized,
    }
  }

  // Handle product status cases
  if (item.status === productStatus.UNAVAILABLE) {
    if (isUnavailableAllDays) {
      return {
        text: t('Unavailable') as string,
        disabled: true,
        loading: !initialized,
      }
    }
    if (isUnavailableSomeDays || isFutureItem) {
      return {
        text: t('Pre-order') as string,
        disabled: false,
        loading: !initialized,
      }
    }
  }

  // Default case
  return { text: t('Add') as string, disabled: false, loading: !initialized }
}

export const getTwoLetterCountryCode = (country: string): string => {
  const regions: Record<string, string> = {
    singapore: 'sg',
    taiwan: 'tw',
    malaysia: 'my',
    vietnam: 'vn',
  }
  return regions[country.toLowerCase()] || ''
}

export const makeRestaurantSchema = ({
  menu,
  meta,
  siteSettingData,
  store,
  currentUrl,
  sections,
}: {
  menu: Oms.MenuCategory[]
  meta: Oms.MetaDataMenuSite
  siteSettingData: Customisation.SiteSettingData
  store: Oms.Store[]
  currentUrl: string
  sections: Sections
}) => {
  const defaultFavicon =
    'https://cdn.oddle.me/wp-content/uploads/2020/09/small-oddle-logo-transparent.png'
  const homePageUrl = new URL(`https:${currentUrl}`)
  const locations: Array<{
    '@type': string
    parentOrganization: {
      name: string
    }
    name: string
    image: string
    telephone: string
    currenciesAccepted: string
    priceRange: string
    address: {
      '@type': string
      streetAddress: string
      addressLocality: string
      addressCountry: string
      addressRegion: string
      postalCode: string
    }
    serveCuisine: string[]
    openingHours: string[]
  }> = []
  const menus: Array<{
    '@type': string
    name: string
    hasMenuItem: Array<{
      '@type': string
      name: string
      description: string
      offers: {
        '@type': string
        price: number | null
        priceCurrency: string
      }
    }>
  }> = []

  store.forEach((s) => {
    const subAddress = s.address.split(',')
    const getAvailableDays = (s.storeHours || []).filter(
      (days) => days.availableDays !== ''
    )
    const openingHours = getAvailableDays.map((days) => {
      const availableDays = days.availableDays.split(',')
      const openingDays = `${availableDays[0]?.trim()} - ${availableDays[
        availableDays.length - 1
      ]?.trim()}`
      return `${openingDays} ${days.availableTimes}`
    })

    const location = {
      '@type': 'restaurant',
      parentOrganization: {
        name: meta.menu.name,
      },
      name: s.name,
      image: siteSettingData.favicon || defaultFavicon,
      telephone: s.phone,
      currenciesAccepted: meta.currencyCode,
      priceRange: '',
      address: {
        '@type': 'PostalAddress',
        streetAddress: `${subAddress[0]} ${subAddress[1]}`,
        addressLocality: subAddress[2]?.trim() || '',
        addressCountry: subAddress[2]?.trim() || '',
        addressRegion: getTwoLetterCountryCode(
          subAddress[2]?.trim()?.toLowerCase() || ''
        ),
        postalCode: subAddress[3] || '',
      },
      serveCuisine: [],
      openingHours,
    }
    locations.push(location)
  })

  menu?.forEach((menuType) => {
    const hasMenu = {
      '@type': 'MenuSection',
      name: menuType.name,
      hasMenuItem: [] as Array<{
        '@type': string
        name: string
        description: string
        offers: {
          '@type': string
          price: number | null
          priceCurrency: string
        }
      }>,
    }
    menuType.items.forEach((item) => {
      const subitems = item.subitems.map(
        (subitem) =>
          `- ${subitem.variants[0] ? subitem.variants[0].variantName : ''}`
      )
      hasMenu.hasMenuItem.push({
        '@type': 'MenuItem',
        name: item.name,
        description: `includes: ${subitems.length ? subitems.join(' ') : ''}`,
        offers: {
          '@type': 'offer',
          price: item.variants[0] ? item.variants[0].price : null,
          priceCurrency: meta.currencyCode,
        },
      })
    })

    menus.push(hasMenu)
  })

  return {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    name: meta?.menu?.name,
    url: (sections.header || {}).homepageUrl || `https://${homePageUrl.host}`,
    logo: siteSettingData.favicon || defaultFavicon,
    location: locations,
    hasMenu: {
      '@type': 'Menu',
      name: 'Delivery Menu',
      description: siteSettingData.homePageMetaDescription,
      hasMenuSection: menus,
    },
  }
}

export const getAllProductNutrigradeTags = (
  tags?: string[]
): string[] | undefined => {
  return tags?.filter((tag) => !tag.includes('nutrigrade'))
}

const calculateSubPrice = (
  item: Oms.Subitems,
  form = {} as Record<string, Record<string, number>>
) => {
  // need to cloneArray to prevent mutation on the original option object passed in from the argument
  const sorted = cloneArray(item.variants)?.sort((a, b) => {
    const aPriceDiff = a.price - a.priceUnderMinimumSelection
    const bPriceDiff = b.price - b.priceUnderMinimumSelection
    return aPriceDiff > bPriceDiff ? 1 : aPriceDiff === bPriceDiff ? 0 : -1
  })

  let sum = 0

  // add 'selectedQuantity' property to option
  item.selectedQuantity = sorted?.reduce((acc, variant) => {
    if (form[item.id]?.[variant.id] !== undefined) {
      variant.quantity = form[item.id]?.[variant.id] || 0 // add 'quantity' property to sub-variant
      const next = acc + variant.quantity
      if (acc >= item.minimumQuantity) {
        sum += variant.price * variant.quantity
      } else if (next <= item.minimumQuantity) {
        sum += variant.priceUnderMinimumSelection * variant.quantity
      } else {
        const withMinPrice = item.minimumQuantity - acc
        const withNormalPrice = variant.quantity - withMinPrice
        sum +=
          variant.priceUnderMinimumSelection * withMinPrice +
          variant.price * withNormalPrice
      }
      return next
    }
    return acc
  }, 0)
  item.totalSubPrice = sum // add 'totalSubPrice' property to option
  return sum
}

export const calculatePrice = (product: Oms.MenuItem, form: any) => {
  // find selected variant from form, first variant is selected by default
  const selectedVariant = product.variants
    ? product.variants.reduce((acc, variant) => {
        variant.selected = variant.id === form.selectedVariant // set 'selected' property to variant object
        if (variant.selected) return variant
        return acc
      }, null as Oms.Variant | null) || product.variants[0]
    : null

  // return 0 if there's no selected variant
  if (!selectedVariant) return 0

  selectedVariant.selected = true // set 'selected' property to variant object

  // add selected variant price to total price
  let total = selectedVariant.price || 0

  // calculate and add total sub price of selected variant's options to total price
  total += selectedVariant.subitems
    ? selectedVariant.subitems.reduce(
        (acc, item) =>
          acc +
          calculateSubPrice(
            item,
            form.subitems as Record<string, Record<string, number>>
          ),
        0
      )
    : 0

  // calculate and add total sub price of options to total price
  total += product.subitems
    ? product.subitems.reduce(
        (acc, item) =>
          acc +
          calculateSubPrice(
            item,
            form.subitems as Record<string, Record<string, number>>
          ),
        0
      )
    : 0

  return total
}

export const getSelectedVariantInfo = (
  variants: Oms.Variant[],
  selectedVariantId?: string | null
) => {
  const { selectedVariant, minVariantPrice } = variants.reduce(
    (acc, variant) => {
      acc.minVariantPrice = Math.min(
        variant.price || 0,
        acc.minVariantPrice || 0
      )
      if (variant.id === selectedVariantId) {
        acc.selectedVariant = variant
      }
      return acc
    },
    { minVariantPrice: variants[0]?.price, selectedVariant: {} } as {
      minVariantPrice: number
      selectedVariant: Oms.Variant
    }
  )

  const selectedVarPriceDiff = (selectedVariant.price || 0) - minVariantPrice

  return { selectedVariant, minVariantPrice, selectedVarPriceDiff }
}

const getOptionsMinimumQuantityFromSubItems = (subitems?: Oms.Subitems[]) =>
  (subitems || []).reduce(
    (obj, subitem) => ({ [subitem.id]: subitem.minimumQuantity, ...obj }),
    {}
  )

export const getOptionsMinimumQuantity = ({
  product,
  selectedVariant,
}: {
  product: Oms.MenuItem
  selectedVariant: string
}): Record<string, number> => {
  const variant =
    product.variants.find((v) => v.id === selectedVariant) ||
    product.variants[0]

  const fromVariant = getOptionsMinimumQuantityFromSubItems(variant?.subitems)
  const fromSubitems = getOptionsMinimumQuantityFromSubItems(product.subitems)
  return { ...fromVariant, ...fromSubitems }
}
